import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostBinding,
  HostListener,
  Inject,
  NgZone,
  effect,
} from '@angular/core';
import {
  faSignOutAlt,
  faSignInAlt,
  faMoon,
  faSun,
  faPalette,
  faBook,
  faCog,
} from '@fortawesome/free-solid-svg-icons';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { NavigationEnd, Router } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';

// --- NEW IMPORTS MSAL v2 ---
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

// Your services and models
import { UserStatusConst } from './Models/userStatus.const';
import { RoleConst } from './Models/roles.const';
import { CurrentUserService } from './Services/CurrentUser/current-user.service';
import { ApplicationInsightsService } from './Services/AppInsight/application-insight.service';
import { MsalInteractionService } from './Services/Auth/msal-interaction.service';
import { ThemeService, ThemePreference } from './theme.service';
import { WhatsNewService } from './Services/WhatsNew/whats-new.service';
import { WHATS_NEW_RELEASE } from './whats-new.config';
import {
  WhatsNewDialogComponent,
  WhatsNewDialogResult,
} from './Components/whats-new-dialog/whats-new-dialog.component';
import { AlertService } from './Services/Alert/alert.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  // Subject to clear subscriptions by destroying the component
  private readonly _destroying$ = new Subject<void>();
  private lastWhatsNewAttemptKey: string | null = null;

  skipLinkPath: string;

  faSignOutAlt = faSignOutAlt;
  faSignInAlt = faSignInAlt;
  faQuestionCircle = faQuestionCircle;
  faMoon = faMoon;
  faSun = faSun;
  faPalette = faPalette;
  faBook = faBook;
  faCog = faCog;

  userAccount: any = null; // Initialize to null
  userRoles: number[];
  // Derived from the user profile; gates the "My Team" nav link the same way the
  // AuthGuard gates the /my-team route.
  isManager: boolean = false;
  hasActiveMyTeamMembers: boolean = false;

  isMobile: boolean = false;
  isCompactNav: boolean = false;
  isMobileHeaderTitle: boolean = false;
  mobileTitle: string = '';

  // Refs + state for the responsive primary nav. Rather than guessing a
  // breakpoint, we measure whether the horizontal links fit and collapse to the
  // menu button when they don't (Material's nav-bar vs nav-menu pattern).
  // `read: ElementRef` is required because #menuToolbar is on <mat-toolbar>, a
  // component — without it the query returns the MatToolbar instance, not the
  // host element, and nativeElement would be undefined.
  @ViewChild('menuToolbar', { read: ElementRef })
  private menuToolbarRef?: ElementRef<HTMLElement>;
  private navResizeObserver?: ResizeObserver;
  private navEvaluationScheduled = false;
  // Width the links last needed; lets us decide when to expand again while
  // collapsed (when the live nav isn't in the DOM to measure). 0 = unknown.
  private requiredNavWidth = 0;
  private compactRequiredNavWidth = 0;

  themePreference: ThemePreference = 'system';
  @HostBinding('class.keyboard-navigation') isKeyboardNavigation = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService, // Switching from BroadcastService to MsalBroadcastService
    public router: Router,
    private curUserService: CurrentUserService,
    private matIconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private appInsights: ApplicationInsightsService,
    private ngZone: NgZone,
    private msalInteractionService: MsalInteractionService,
    private themeService: ThemeService,
    private dialog: MatDialog,
    private whatsNewService: WhatsNewService,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService
  ) {
    curUserService.user$.pipe(takeUntil(this._destroying$)).subscribe((data) => {
      this.ngZone.run(() => this.userUpdate(data));
    });

    const isIE =
      window.navigator.userAgent.indexOf('MSIE ') > -1 ||
      window.navigator.userAgent.indexOf('Trident/') > -1;

    if (isIE) this.router.navigateByUrl('unsupported-browser');

    effect(() => {
      this.themePreference = this.themeService.preference();
    });
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (!this.router.url.endsWith('#main-content')) {
          this.skipLinkPath = `${this.router.url}#main-content`;
        }
      });

    // --- MSAL LOGIC V2: Session Management ---
    // We listen for the interaction status. When it's 'None', it means the login/redirect has finished.
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });

    // Icon Settings
    let iconFolder = './assets/ontario-design-system/icons/svg/';
    this.matIconRegistry.addSvgIconInNamespace(
      'ontario',
      'upload',
      this.sanitizer.bypassSecurityTrustResourceUrl(iconFolder + 'ontario-icon-upload.svg')
    );
    this.matIconRegistry.addSvgIconInNamespace(
      'ontario',
      'warning',
      this.sanitizer.bypassSecurityTrustResourceUrl(iconFolder + 'ontario-icon-alert-warning.svg')
    );
    this.matIconRegistry.addSvgIconInNamespace(
      'ontario',
      'pin-location-on',
      this.sanitizer.bypassSecurityTrustResourceUrl(iconFolder + 'ontario-icon-pin-location-on.svg')
    );
    this.matIconRegistry.addSvgIconInNamespace(
      'ontario',
      'calendar',
      this.sanitizer.bypassSecurityTrustResourceUrl(iconFolder + 'ontario-icon-calendar.svg')
    );
    this.matIconRegistry.addSvgIconInNamespace(
      'ontario',
      'search',
      this.sanitizer.bypassSecurityTrustResourceUrl(iconFolder + 'ontario-icon-search.svg')
    );
    this.matIconRegistry.addSvgIconInNamespace(
      'material',
      'dual-monitor',
      this.sanitizer.bypassSecurityTrustResourceUrl(
        './assets/images/material-icon-dual-monitor.svg'
      )
    );

    this.isMobile = window.innerWidth < 1024;
    this.isMobileHeaderTitle = window.innerWidth < 810;

    this.router.events.pipe(take(1)).subscribe((event: any) => {
      this.setMobileTitle(event.url);
    });
  }

  /**
   * Checks if there is an active account in the MSAL instance.
   * If not, it attempts to set the first available account as active.
   * Once an account is active, it triggers the user profile update.
   */
  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }

    if (activeAccount) {
      this.ngZone.run(() => {
        this.userAccount = activeAccount;
        // We only attempt to update the database user if we already have a valid Active Directory session.
        this.curUserService.updateUser();
      });
    }
  }

  // Subscription cleanup
  ngOnDestroy(): void {
    this.navResizeObserver?.disconnect();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  userUpdate(user: any) {
    if (user?.userStatusId == UserStatusConst.Active) {
      this.maybeOpenWhatsNewModal(user);
      this.userRoles = [];
      if (user?.userRoles) {
        for (let role of user.userRoles) {
          this.userRoles.push(role.roleTypeId);
        }
      }
      this.isManager = user?.isManager === true;
      this.hasActiveMyTeamMembers = user?.hasActiveMyTeamMembers === true;
    } else {
      this.userRoles = null;
      this.isManager = false;
      this.hasActiveMyTeamMembers = false;
    }
    // The visible nav items depend on the user's roles, so the required width
    // changed — force a fresh measurement.
    this.requiredNavWidth = 0;
    this.compactRequiredNavWidth = 0;
    this.scheduleNavEvaluation();
  }

  private maybeOpenWhatsNewModal(user: any): void {
    const userId: number | null = user?.id ?? null;
    if (!userId) return;

    const scope = this.whatsNewService.getScope(user);

    // Prevent re-opening due to repeated user$ emissions within the same session.
    const attemptKey = `${userId}:${scope}:${WHATS_NEW_RELEASE.id}`;
    if (this.lastWhatsNewAttemptKey === attemptKey) return;
    this.lastWhatsNewAttemptKey = attemptKey;

    if (!this.whatsNewService.shouldShow(userId, scope)) return;

    const release = this.whatsNewService.getReleaseFor(scope);
    if (!release.items.length) return;

    const dialogRef = this.dialog.open<
      WhatsNewDialogComponent,
      typeof WHATS_NEW_RELEASE,
      WhatsNewDialogResult
    >(WhatsNewDialogComponent, {
      data: release,
      disableClose: true,
      autoFocus: false,
      panelClass: 'whats-new-dialog-panel',
      width: '720px',
      maxWidth: '92vw',
      // Keep the action row inside the visible viewport; the panel class upgrades
      // this to dynamic viewport units for mobile browsers.
      maxHeight: '90vh',
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result === 'acknowledged' || result === 'learn-more') {
          this.whatsNewService.acknowledge(userId, scope);
        }
      });
  }

  login() {
    // Use the Guard configuration or a simple redirect
    if (this.msalGuardConfig.authRequest) {
      this.msalInteractionService.clearStaleInteraction();
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalInteractionService.clearStaleInteraction();
      this.msalService.loginRedirect();
    }
  }

  logout() {
    // Explicit Logout Redirect
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
    this.userAccount = null;
  }

  setMobileMenu(val: string) {
    this.mobileTitle = val;
    this.alertService.clearAlert();
  }

  // Helper for the mobile title switch
  setMobileTitle(url: string) {
    if (!url) return;

    if (url.includes('/home')) this.mobileTitle = 'Home';
    else if (url.includes('/reservations')) this.mobileTitle = 'My bookings';
    else if (url.includes('/workstations')) this.mobileTitle = 'Manage workstations';
    else if (url.includes('/buildings')) this.mobileTitle = 'Manage buildings';
    else if (url.includes('/users')) this.mobileTitle = 'Manage users';
    else if (url.includes('/reports')) this.mobileTitle = 'Extract report';
    else if (url.includes('/divisions')) this.mobileTitle = 'Manage divisions';
  }

  // --- Authorization Functions (No logical changes, only cleaning) ---

  authorizeHome(): boolean {
    return this.hasRole([
      RoleConst.User,
      RoleConst.SpaceAdmin,
      RoleConst.UserAdmin,
      RoleConst.ReportAdmin,
      RoleConst.SuperUser,
    ]);
  }

  authorizeMyTeam(): boolean {
    // Mirrors the /my-team AuthGuard: a regular user with active My Team members.
    return this.isManager && this.hasActiveMyTeamMembers && this.hasRole([RoleConst.User]);
  }

  authorizeUsers(): boolean {
    return this.hasRole([RoleConst.UserAdmin, RoleConst.SuperUser]);
  }

  authorizeSpace(): boolean {
    return this.hasRole([RoleConst.SpaceAdmin, RoleConst.SuperUser]);
  }

  authorizeDivision(): boolean {
    return this.hasRole([RoleConst.SuperUser]);
  }

  authorizeSpaceAdminDivision(): boolean {
    return this.hasRole([RoleConst.SpaceAdmin]);
  }

  authorizeReport(): boolean {
    return this.hasRole([RoleConst.ReportAdmin, RoleConst.SuperUser]);
  }

  authorizeBuildings(): boolean {
    return this.hasRole([RoleConst.SuperUser]);
  }

  authorizeSpaceAdminBuildings(): boolean {
    return this.hasRole([RoleConst.SpaceAdmin]);
  }

  authorizeSuperUser(): boolean {
    return this.hasRole([RoleConst.SuperUser]);
  }

  // Helper to reduce repetitive code
  private hasRole(allowedRoles: number[]): boolean {
    if (!this.userRoles) return false;
    return this.userRoles.some((r) => allowedRoles.includes(r));
  }

  setDarkMode() {
    this.themeService.setDarkTheme();
  }

  setLightMode() {
    this.themeService.setLightTheme();
  }

  setSystemMode() {
    this.themeService.setSystemTheme();
  }

  editProfile() {
    this.router.navigateByUrl('/edit-profile');
  }

  viewUserGuide() {
    this.router.navigateByUrl('/docs');
  }

  @HostListener('window:resize')
  onResize() {
    this.isMobileHeaderTitle = window.innerWidth < 810;
    // The horizontal-vs-menu decision is made by measuring, not by width.
    this.scheduleNavEvaluation();
  }

  ngAfterViewInit(): void {
    this.ensureNavObserver();
    this.scheduleNavEvaluation();
  }

  /**
   * Attach the ResizeObserver to the nav toolbar so we re-measure whenever its
   * available width changes (resize, zoom, devtools docking, etc.). The toolbar
   * lives behind an *ngIf and may mount after ngAfterViewInit, so this is also
   * called lazily once the ref becomes available.
   */
  private ensureNavObserver(): void {
    if (this.navResizeObserver || typeof ResizeObserver === 'undefined') return;
    const toolbar = this.menuToolbarRef?.nativeElement;
    if (!toolbar) return;
    this.navResizeObserver = new ResizeObserver(() => this.scheduleNavEvaluation());
    this.navResizeObserver.observe(toolbar);
  }

  /**
   * Coalesce evaluation requests into a single measurement per animation frame.
   * Deferring also avoids a synchronous ResizeObserver feedback loop.
   */
  private scheduleNavEvaluation(): void {
    if (this.navEvaluationScheduled) return;
    this.navEvaluationScheduled = true;
    requestAnimationFrame(() => {
      this.navEvaluationScheduled = false;
      this.evaluateNavLayout();
    });
  }

  /**
   * Decide whether the primary nav shows as horizontal links or collapses to
   * the menu button, based on whether the links actually fit the toolbar.
   *
   * The toolbar's box size doesn't change between the two states, so toggling
   * here does not retrigger the ResizeObserver — no layout loop.
   */
  private evaluateNavLayout(): void {
    // Nav only renders for a signed-in user with roles.
    if (!this.userAccount || !this.userRoles) return;
    const toolbar = this.menuToolbarRef?.nativeElement;
    if (!toolbar) return;
    // Toolbar exists now — make sure we're observing it for future resizes.
    this.ensureNavObserver();

    const available = this.availableNavWidth(toolbar);

    if (!this.isMobile) {
      // Expanded: measure the regular links first, then the compact links
      // before collapsing to the menu.
      const nav = toolbar.querySelector('.nav-tabs') as HTMLElement | null;
      if (!nav) return;
      const regularRequired = this.measureRequiredNavWidth(nav, false);
      const compactRequired = this.measureRequiredNavWidth(nav, true);
      if (regularRequired > 0) this.requiredNavWidth = regularRequired;
      if (compactRequired > 0) this.compactRequiredNavWidth = compactRequired;

      const useCompact = regularRequired > available + 1 && compactRequired <= available + 1;
      const useMobile = compactRequired > available + 1;
      if (this.isCompactNav !== useCompact || this.isMobile !== useMobile) {
        this.isCompactNav = useCompact;
        this.isMobile = useMobile;
        this.cdr.detectChanges();
      }
      return;
    }

    // Collapsed: the links aren't rendered, so we can't measure them directly.
    if (this.requiredNavWidth === 0) {
      // Requirement unknown (e.g. app opened on a narrow viewport or roles just
      // changed): render the links once to capture it, then settle the state.
      this.isMobile = false;
      this.cdr.detectChanges();
      this.isCompactNav = false;
      const nav = toolbar.querySelector('.nav-tabs') as HTMLElement | null;
      const required = nav ? this.measureRequiredNavWidth(nav, false) : 0;
      const compactRequired = nav ? this.measureRequiredNavWidth(nav, true) : 0;
      if (required > 0) this.requiredNavWidth = required;
      if (compactRequired > 0) this.compactRequiredNavWidth = compactRequired;
      this.isCompactNav = required > available + 1 && compactRequired <= available + 1;
      this.isMobile = compactRequired > available + 1;
      this.cdr.detectChanges();
      return;
    }

    // Otherwise compare the room now available against what the links need.
    // A small buffer adds hysteresis so we don't flip-flop at the threshold.
    if (available >= this.compactRequiredNavWidth + 16) {
      this.isCompactNav = available < this.requiredNavWidth + 16;
      this.isMobile = false;
      this.cdr.detectChanges();
      // Confirm the fit now that the links are live (covers content changes).
      this.evaluateNavLayout();
    }
  }

  /** Total width the horizontal links need: sum of each link + the row gaps. */
  private measureRequiredNavWidth(nav: HTMLElement, compact: boolean): number {
    const links = Array.from(nav.children) as HTMLElement[];
    if (links.length === 0) return 0;
    const hadCompactClass = nav.classList.contains('nav-tabs-compact');
    nav.classList.toggle('nav-tabs-compact', compact);
    const gap = parseFloat(getComputedStyle(nav).columnGap) || 0;
    const linksWidth = links.reduce((sum, link) => sum + link.offsetWidth, 0);
    nav.classList.toggle('nav-tabs-compact', hadCompactClass);
    return linksWidth + gap * (links.length - 1);
  }

  /**
   * Room actually available to the links: the viewport width minus the toolbar's
   * side gutter. We measure against the viewport (not the toolbar's clientWidth)
   * because the page can be wider than the window; the toolbar then stretches
   * past the visible edge and would falsely report plenty of room.
   */
  private availableNavWidth(toolbar: HTMLElement): number {
    const styles = getComputedStyle(toolbar);
    const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
    return document.documentElement.clientWidth - paddingX;
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      this.isKeyboardNavigation = true;
    }
  }

  @HostListener('window:pointerdown', ['$event'])
  onWindowPointerDown(event: PointerEvent) {
    if (
      event.pointerType === 'mouse' ||
      event.pointerType === 'touch' ||
      event.pointerType === 'pen'
    ) {
      this.isKeyboardNavigation = false;
    }
  }
}
//
